import React, { FC, useEffect } from "react";

import { basketActiveAtom, basketSelector, useBasketList } from "@keepeek/commons";
import {
  BasketToggler as BasketTogglerComp,
  useThemeColors,
  withSuspense,
} from "@keepeek/refront-components";
import { useMediaQuery, useTheme } from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { focusSelector } from "../../../lib/focusSelector";
import { currentBasketIdState } from "../../../providers/basket/atoms";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";

export type BasketTogglerProps = {
  showCount?: boolean;
  backgroundColor?: string;
  color?: string;
};

const BasketToggler: FC<React.PropsWithChildren<BasketTogglerProps>> = function ({
  showCount = true,
  backgroundColor,
  color,
}) {
  const [activeBasket, setActiveBasket] = useRecoilState(basketActiveAtom);
  const [currentBasketId, setCurrentBasketId] = useRecoilState(currentBasketIdState);
  const { activateBasketPartitioning } = useRecoilValue(configSectionComponentBasketSelector) ?? {
    activateBasketPartitioning: false,
  };
  const { basketList } = useBasketList({ basketPartitioning: activateBasketPartitioning });
  const currentBasket = useRecoilValue(basketSelector({ id: currentBasketId }));
  const { convertColor } = useThemeColors();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (basketList.length > 0 && currentBasketId === undefined) {
      setCurrentBasketId(basketList[0].id);
    }
  }, [basketList, currentBasketId, setCurrentBasketId]);

  function handleToggle() {
    setActiveBasket((prevState) => !prevState);
    if (!activeBasket) {
      // set the focus only when the basket is visible
      // wait a bit just to be sure that all animations have at least started
      setTimeout(() => {
        const focusable = focusSelector(document.getElementById("Basket"));
        focusable?.focus();
      }, 100);
    }
  }
  return (
    <BasketTogglerComp
      count={currentBasket?.mediaCount || 0}
      icon={<KIcon className={"fa-solid fa-basket-shopping-simple"} />}
      active={activeBasket}
      onToggle={handleToggle}
      showCount={showCount}
      className={"BasketToggler"}
      backgroundColor={convertColor(backgroundColor)}
      color={convertColor(color)}
      sx={{ margin: isMdDown ? 0 : "" }}
    />
  );
};

export default withSuspense(BasketToggler);
